import { ReactNode } from 'react'
import { Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { OverlayState } from '@/hooks/useOverlay'
import { useCurrentAccountContext } from '@/components/accounts/CurrentAccountProvider'
import CopyIDButton from '@/components/copy-id-button/CopyIDButton'
import { Dialog, DialogContent, DialogFooter } from '@/components/dialog'

interface AppXchangeInstructionsDialogProps {
  overlay: OverlayState
  credentialId: string
  integrationName: string
}

type StepProps = { number: number; children: ReactNode }

function Step({ number, children }: StepProps) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Chip
        label={number}
        size="small"
        color="primary"
        variant="outlined"
        sx={{ fontWeight: 700, minWidth: 28, height: 24, flexShrink: 0 }}
      />
      <Typography variant="body2" sx={{ pt: '2px' }}>
        {children}
      </Typography>
    </Stack>
  )
}

export default function AppXchangeInstructionsDialog({
  overlay,
  credentialId,
  integrationName,
}: AppXchangeInstructionsDialogProps) {
  const { rootOrganization } = useCurrentAccountContext()
  return (
    <Dialog title="Setup Instructions" maxWidth="sm" fullWidth {...overlay}>
      <DialogContent>
        <Stack spacing={3}>
          {/* Section 1: AppXchange */}
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              Connect Pixydocs in AppXchange
            </Typography>

            <Step number={1}>
              Copy your Credential ID from Pixydocs:{' '}
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                component="span"
                sx={{ display: 'inline-flex', verticalAlign: 'middle' }}
              >
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'action.hover',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                  }}
                >
                  {credentialId}
                </Typography>
                <CopyIDButton
                  stringToCopy={credentialId}
                  asIconButton
                  size="small"
                  label="Copy Credential ID"
                />
              </Stack>
            </Step>

            <Step number={2}>
              Open your AppXchange Workspace and go to the{' '}
              <strong>Integrations</strong> tab.
            </Step>

            <Step number={3}>
              Inside the <strong>Pixydocs Connector</strong>, click{' '}
              <strong>Add Connection</strong> for a new connection, or click{' '}
              <strong>Pixydocs</strong> to reconfigure an existing one.
            </Step>

            <Step number={4}>
              Click the <strong>Create New</strong> tab and scroll down to{' '}
              <strong>Connection Details</strong>.
            </Step>

            <Step number={5}>
              Enter a name for the connection, such as{' '}
              <strong>Pixydocs Connection</strong>.
            </Step>

            <Step number={6}>
              Under <strong>OAuth Client</strong>, select{' '}
              <strong>Pixydocs</strong>.
            </Step>

            <Step number={7}>
              Under <strong>Environment</strong>, select{' '}
              <strong>Production</strong>.
            </Step>

            <Step number={8}>
              Paste your Credential ID into the <strong>Credential ID</strong>{' '}
              field.
            </Step>

            <Step number={9}>
              Click <strong>Connect</strong> and wait for the connection to
              succeed.
            </Step>

            <Step number={10}>
              Click <strong>Test Connection</strong> and confirm the test
              passes.
            </Step>
          </Stack>

          <Divider />

          {/* Section 2: Pixydocs */}
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={600}>
              Finish Configuration in Pixydocs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These steps only apply if you use companies within{' '}
              <strong>{integrationName}</strong>.
            </Typography>
            <Stack component="ul" spacing={1} sx={{ m: 0, pl: 2.5 }}>
              <Typography variant="body2" component="li">
                On the <strong>{integrationName}</strong> Integration page,
                create the required companies.
              </Typography>
              <Typography variant="body2" component="li">
                Each company will appear as a child Organization in your project
                hierarchy.
              </Typography>
              <Typography variant="body2" component="li">
                When processing documents, you can assign them directly to a
                company&apos;s organization when uploading or sending via email.
                Alternatively, if the company name appears on the document,
                enable the Match Company workflow state and drop documents into{' '}
                <strong>{rootOrganization.name}</strong>
                &mdash;Pixydocs will route them automatically.
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogFooter>
        <Button onClick={overlay.close} variant="text">
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
